/**
 * @prettier
 */

import React, { useCallback } from 'react';
import cx from 'classnames';
import {
  RenderCheckBoxField,
  ButtonV2,
  RenderField,
  Dropdown as ZolaUIDropdown,
} from '@zola/zola-ui';
import styles from './styles.module.less';

const Header = ({ children }) => {
  return <div className={styles.header}>{children}</div>;
};

const Text = ({ children }) => {
  return <div className={styles.text}>{children}</div>;
};

const Spacer = ({ hideOnMobile }) => {
  return <div className={cx(styles.spacer, { [styles.hideOnMobile]: hideOnMobile })} />;
};

const InputGroup = ({ columnOnMobile, children }) => {
  return (
    <div className={cx(styles.inputGroup, { [styles.columnOnMobile]: columnOnMobile })}>
      {children}
    </div>
  );
};

const Field = ({ className, small, ...props }) => {
  return (
    <div className={cx(className, styles.field, { [styles.small]: small })}>
      <RenderField {...props} input={{ ...props.input, onBlur: () => {} }} />
    </div>
  );
};

const DateField = ({ label, inputName, placeholder, disabled, dateIsValid, value, onChange }) => {
  return (
    <Field
      type="date"
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      className={cx({ [styles.invalidInput]: !dateIsValid })}
      input={{
        name: inputName,
        value: value || '',
        onChange,
      }}
    />
  );
};

const Checkbox = ({ className, id, disabled, label, checked, onChange }) => {
  return (
    <RenderCheckBoxField
      className={cx(styles.checkbox, className)}
      id={id}
      label={label}
      type="checkbox"
      disabled={disabled}
      input={{
        checked,
        onChange: e => onChange(e.target.checked),
      }}
      meta={{ touched: true }}
    />
  );
};

const Dropdown = ({ required, name, label, options, selected, onSelect }) => {
  /**
   * The zola-ui Dropdown component is uncontrolled so we display the selected
   * or default label regardless of the internel state of the Dropdown component.
   */
  const selectedOption = options.find(option => option.value === selected);

  const dropdownLabel = (selectedOption && selectedOption.label) || label;

  return (
    <div className={styles.dropdown}>
      <label>
        {label}
        {required && <span className={styles.requiredLabel}>*</span>}
      </label>
      <ZolaUIDropdown
        onSelect={onSelect}
        facetList={{ key: name, label: dropdownLabel, options }}
        className={styles.defensiveDropdownFix}
      />
    </div>
  );
};

const FormButtons = ({
  className,
  backLabel,
  nextLabel,
  nextDisabled,
  backDisabled,
  backClassName,
  nextClassName,
  onBack,
  onNext,
}) => {
  return (
    <div className={cx(styles.formButtonBox, className)}>
      <ButtonV2
        variant="secondary"
        className={cx(styles.backButton, backClassName)}
        disabled={backDisabled}
        onClick={onBack}
      >
        {backLabel}
      </ButtonV2>
      <Spacer />
      <ButtonV2
        type="submit"
        variant="primary"
        className={cx(styles.nextButton, nextClassName)}
        disabled={nextDisabled}
        onClick={onNext}
      >
        {nextLabel}
      </ButtonV2>
    </div>
  );
};

const Form = ({ onSubmit, children }) => {
  const submit = useCallback(
    event => {
      event.preventDefault();
      onSubmit(event);
    },
    [onSubmit]
  );

  return <form onSubmit={submit}>{children}</form>;
};

const PreviewBox = ({ children }) => {
  return <div className={styles.previewBox}>{children}</div>;
};

const Row = ({ children }) => {
  return <div className={styles.row}>{children}</div>;
};

const Col = ({ children }) => {
  return <div className={styles.col}>{children}</div>;
};

const Box = ({ children }) => {
  return <div className={styles.box}>{children}</div>;
};

export default {
  Header,
  Text,
  Spacer,
  InputGroup,
  Field,
  DateField,
  Checkbox,
  Dropdown,
  FormButtons,
  Form,
  PreviewBox,
  Row,
  Col,
  Box,
};



// WEBPACK FOOTER //
// ./src/cards/components/common/SingleSample/Layout/Layout.jsx